
import {
  createDateCurrentMinusFiveDays,
  createDateCurrentPlusThreeDays,
} from '../utils/dateTimeHelpers';

const DMY_REGEX = /^(\d{2})\.(\d{2})\.(\d{4})$/;
const HMS_REGEX = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/;

function pad(n) {
  return String(n).padStart(2, '0');
}

function parseDMY(str) {
  if (!str) return null;
  const match = str.trim().match(DMY_REGEX);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const d = +dd, m = +mm, y = +yyyy;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

function parseHMS(str) {
  if (!str) return null;
  const match = str.trim().match(HMS_REGEX);
  if (!match) return null;
  const [, h, m, s] = match;
  const hh = +h, mm = +m, ss = +s;
  if (hh > 23 || mm > 59 || ss > 59) return null;
  return {h: pad(hh), m: pad(mm), s: pad(ss)};
}

function formatDMY(date) {
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

function formatDisplay(date) {
  return `${formatDMY(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatApi(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatISODate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function isoToDMY(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function dmyToISO(str) {
  const date = parseDMY(str);
  return date ? formatISODate(date) : null;
}

function toParts(date) {
  return {h: pad(date.getHours()), m: pad(date.getMinutes()), s: pad(date.getSeconds())};
}

function clamp(date, min, max) {
  if (date < min) return new Date(min);
  if (date > max) return new Date(max);
  return date;
}

export default {
  name: 'MassReparsePeriod',

  data() {
    return {
      menuOpen: false,
      rangeDates: [],
      timeParts: {
        start: {h: '00', m: '00', s: '00'},
        end: {h: '23', m: '59', s: '59'},
      },
      focusedEdge: 'start',
      appliedPreset: null,

      draft: {
        startDate: '',
        startTime: '00:00:00',
        endDate: '',
        endTime: '23:59:59',
      },

      presets: [
        {kind: 'today', label: 'Сегодня'},
        {kind: 'yesterday', label: 'Вчера'},
        {kind: '5days', label: '5 дней'},
      ],
    };
  },

  computed: {
    minDateBound() {
      return createDateCurrentMinusFiveDays();
    },
    maxDateBound() {
      return createDateCurrentPlusThreeDays();
    },
    minDateISO() {
      return formatISODate(this.minDateBound);
    },
    maxDateISO() {
      return formatISODate(this.maxDateBound);
    },
    sortedRange() {
      return [...this.rangeDates].sort();
    },
    startDate() {
      const iso = this.sortedRange[0];
      if (!iso) return null;
      const [y, m, d] = iso.split('-').map(Number);
      const p = this.timeParts.start;
      return new Date(y, m - 1, d, +p.h, +p.m, +p.s);
    },
    endDate() {
      const iso = this.sortedRange[1];
      if (!iso) return null;
      const [y, m, d] = iso.split('-').map(Number);
      const p = this.timeParts.end;
      return new Date(y, m - 1, d, +p.h, +p.m, +p.s);
    },
    canSubmit() {
      if (!this.startDate || !this.endDate) return false;
      if (this.startDate > this.endDate) return false;
      if (this.startDate < this.minDateBound || this.startDate > this.maxDateBound) return false;
      if (this.endDate < this.minDateBound || this.endDate > this.maxDateBound) return false;
      return true;
    },
    triggerLabel() {
      if (!this.startDate || !this.endDate) return 'Выбрать период';
      if (this.appliedPreset) {
        const preset = this.presets.find(p => p.kind === this.appliedPreset);
        if (preset) return preset.label;
      }
      return `${formatDisplay(this.startDate)}  —  ${formatDisplay(this.endDate)}`;
    },
    activeClock: {
      get() {
        const p = this.timeParts[this.focusedEdge];
        return `${p.h}:${p.m}:${p.s}`;
      },
      set(value) {
        const [h, m, s] = value.split(':');
        this.timeParts[this.focusedEdge] = {h: pad(+h), m: pad(+m), s: pad(+(s || 0))};
      },
    },
  },

  watch: {
    rangeDates: {
      handler() {
        this.appliedPreset = null;
        const [from, to] = this.sortedRange;
        this.draft.startDate = isoToDMY(from);
        this.draft.endDate = isoToDMY(to);
      },
      immediate: true,
    },
    timeParts: {
      deep: true,
      handler(val) {
        this.appliedPreset = null;
        this.draft.startTime = `${val.start.h}:${val.start.m}:${val.start.s}`;
        this.draft.endTime = `${val.end.h}:${val.end.m}:${val.end.s}`;
      },
      immediate: true,
    },
    'draft.startDate'(val) {
      const iso = dmyToISO(val);
      if (!iso) return;
      if (this.rangeDates[0] === iso) return;
      const next = [...this.rangeDates];
      next[0] = iso;
      if (next.length < 2) next.push(iso);
      this.rangeDates = next;
    },
    'draft.endDate'(val) {
      const iso = dmyToISO(val);
      if (!iso) return;
      if (this.rangeDates[1] === iso) return;
      const next = [...this.rangeDates];
      if (next.length < 2) next.push(iso);
      else next[1] = iso;
      this.rangeDates = next;
    },
    'draft.startTime'(val) {
      const parts = parseHMS(val);
      if (!parts) return;
      const cur = this.timeParts.start;
      if (cur.h === parts.h && cur.m === parts.m && cur.s === parts.s) return;
      this.timeParts.start = parts;
    },
    'draft.endTime'(val) {
      const parts = parseHMS(val);
      if (!parts) return;
      const cur = this.timeParts.end;
      if (cur.h === parts.h && cur.m === parts.m && cur.s === parts.s) return;
      this.timeParts.end = parts;
    },
  },

  methods: {
    applyPreset(kind) {
      const now = new Date();
      let from;
      let to;

      if (kind === 'today') {
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      } else if (kind === 'yesterday') {
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
      } else if (kind === '5days') {
        from = new Date(now);
        from.setDate(from.getDate() - 5);
        from.setHours(0, 0, 0, 0);
        to = new Date(now);
      }

      from = clamp(from, this.minDateBound, this.maxDateBound);
      to = clamp(to, this.minDateBound, this.maxDateBound);

      this.rangeDates = [formatISODate(from), formatISODate(to)];
      this.timeParts.start = toParts(from);
      this.timeParts.end = toParts(to);
      this.$nextTick(() => { this.appliedPreset = kind; });
    },

    resetPopup() {
      this.rangeDates = [];
      this.timeParts.start = {h: '00', m: '00', s: '00'};
      this.timeParts.end = {h: '23', m: '59', s: '59'};
    },

    validateDate(value) {
      if (!value) return true;
      const date = parseDMY(value);
      if (!date) return 'Формат: ДД.ММ.ГГГГ';
      if (date < new Date(this.minDateBound.getFullYear(), this.minDateBound.getMonth(), this.minDateBound.getDate())) {
        return 'Не раньше 5 дней назад.';
      }
      if (date > this.maxDateBound) return 'Не позже 3 дней вперёд.';
      return true;
    },

    validateTime(value) {
      if (!value) return true;
      if (!parseHMS(value)) return 'Формат: ЧЧ:ММ:СС';
      return true;
    },

    reset() {
      this.rangeDates = [];
      this.timeParts.start = {h: '00', m: '00', s: '00'};
      this.timeParts.end = {h: '23', m: '59', s: '59'};
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },

    submit() {
      if (!this.canSubmit) return;
      this.$emit('submit', {
        from: formatApi(this.startDate),
        to: formatApi(this.endDate),
      });
    },
  },
};
